/**
 * ============================================
 * USER GROWTH CHART
 * ============================================
 * 
 * Shows new user signups over time with scrollable timeline.
 * Similar to stock charts with day/month/year granularity.
 * Shows NEW users per period (not cumulative).
 * Supports filtering by platform.
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine
} from 'recharts';
import { TrendingUp, UserPlus } from 'lucide-react';
import { BACKEND_URL } from '@/config';
import { MARKER_CHART_MARGIN, markerLabel } from '@/components/common/ChartMarkers';

interface UserGrowthDataPoint {
  timestamp: number;
  newUsers: number;
  totalUsers: number;
}

interface UserGrowthChartProps {
  environmentId: string;
}

const GRANULARITY_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
];

const PLATFORM_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pc', label: 'PC' },
  { value: 'playstation', label: 'PlayStation' },
  { value: 'ios', label: 'iOS' },
];

function formatCount(count: number): string {
  if (count >= 1000000) {
    const value = count / 1000000;
    return value % 1 === 0 ? `${value}M` : `${value.toFixed(1)}M`;
  }
  if (count >= 1000) {
    const value = count / 1000;
    return value % 1 === 0 ? `${value}K` : `${value.toFixed(1)}K`;
  }
  return count.toString();
}

// Round up to nearest 200
function getYAxisMax(maxValue: number): number {
  if (maxValue <= 0) return 200;
  return Math.ceil(maxValue / 200) * 200;
}

// Generate Y-axis ticks in increments of 200
function getYAxisTicks(maxValue: number): number[] {
  const max = getYAxisMax(maxValue);
  const ticks: number[] = [];
  for (let i = 0; i <= max; i += 200) {
    ticks.push(i);
  }
  return ticks;
}

const tooltipStyle = {
  backgroundColor: '#1f2937',
  border: '1px solid #374151',
  borderRadius: '8px',
  padding: '8px 12px',
  color: '#f9fafb',
};

export function UserGrowthChart({ environmentId }: UserGrowthChartProps) {
  const [data, setData] = useState<UserGrowthDataPoint[]>([]);
  const [granularity, setGranularity] = useState<string>('day');
  const [platform, setPlatform] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/v1/environments/${environmentId}/user-growth?granularity=${granularity}&platform=${platform}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.error) {
          setError(result.error);
          setData([]);
        } else {
          setData(result.data || []);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [environmentId, granularity, platform]);

  // Scroll to end when data loads (show most recent)
  useEffect(() => {
    if (scrollContainerRef.current && data.length > 0) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
    }
  }, [data]);

  const formatXAxis = (timestamp: number) => {
    const date = new Date(timestamp);
    if (granularity === 'year') {
      return date.getFullYear().toString();
    } else if (granularity === 'month') {
      return date.toLocaleDateString([], { month: 'short', year: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const formatTooltipLabel = (timestamp: number) => {
    const date = new Date(timestamp);
    if (granularity === 'year') {
      return date.getFullYear().toString();
    } else if (granularity === 'month') {
      return date.toLocaleDateString([], { month: 'long', year: 'numeric' });
    } else {
      return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  // Calculate chart width based on data points for scrolling
  const getChartWidth = () => {
    const minWidth = 100; // percentage
    const pointsPerScreen = granularity === 'day' ? 30 : granularity === 'month' ? 12 : 5;
    const dataPoints = data.length;
    
    if (dataPoints <= pointsPerScreen) {
      return '100%';
    }
    return `${Math.max(minWidth, (dataPoints / pointsPerScreen) * 100)}%`;
  };

  // Get stats
  const latestData = data.length > 0 ? data[data.length - 1] : null;
  const totalNewUsers = data.reduce((sum, d) => sum + d.newUsers, 0);
  const avgNewUsers = data.length > 0 ? Math.round(totalNewUsers / data.length) : 0;
  const maxNewUsers = data.length > 0 ? Math.max(...data.map(d => d.newUsers)) : 0;
  const yAxisMax = getYAxisMax(maxNewUsers);
  const yAxisTicks = getYAxisTicks(maxNewUsers);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-5 w-5" />
              New User Signups
              {latestData && (
                <span className="text-sm font-normal text-muted-foreground">
                  (Avg: {formatCount(avgNewUsers)}/{granularity})
                </span>
              )}
            </CardTitle>
            
            {/* Granularity selector */}
            <div className="flex items-center gap-1">
              {GRANULARITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setGranularity(option.value)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    granularity === option.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Platform filter */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-muted-foreground mr-1">Platform:</span>
            {PLATFORM_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setPlatform(option.value)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  platform === option.value
                    ? 'bg-green-500 text-white'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="h-[200px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Loading signup data...</span>
          </div>
        ) : error ? (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
            {error}
          </div>
        ) : data.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
            No signup data available
          </div>
        ) : (
          <>
            {/* Stats summary */}
            <div className="flex items-center gap-4 mb-3 text-sm flex-wrap">
              <div className="flex items-center gap-1">
                <UserPlus className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Latest:</span>
                <span className="font-semibold text-green-600 dark:text-green-500">+{formatCount(latestData?.newUsers || 0)}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Period total:</span>
                <span className="font-semibold">{formatCount(totalNewUsers)}</span>
              </div>
            </div>
            
            {/* Scrollable chart container */}
            <div 
              ref={scrollContainerRef}
              className="overflow-x-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent"
              style={{ scrollBehavior: 'smooth' }}
            >
              <div style={{ width: getChartWidth(), minWidth: '100%', height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data} margin={{ ...MARKER_CHART_MARGIN, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={formatXAxis}
                      tick={{ fontSize: 10 }}
                      stroke="#6b7280"
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      domain={[0, yAxisMax]}
                      ticks={yAxisTicks}
                      tick={{ fontSize: 10 }}
                      stroke="#6b7280"
                      tickFormatter={formatCount}
                      tickLine={false}
                      axisLine={false}
                      width={50}
                    />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      labelFormatter={formatTooltipLabel}
                      formatter={(value: number) => [
                        `+${formatCount(value)}`, 
                        'New Users'
                      ]}
                    />
                    {/* ATH marker */}
                    {maxNewUsers > 0 && (
                      <ReferenceLine y={maxNewUsers} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5}
                        label={markerLabel(`ATH ${formatCount(maxNewUsers)}`, '#ef4444', 'above')} />
                    )}
                    {/* Current marker */}
                    {latestData && latestData.newUsers > 0 && latestData.newUsers !== maxNewUsers && (
                      <ReferenceLine y={latestData.newUsers} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
                        label={markerLabel(`Now ${formatCount(latestData.newUsers)}`, '#22c55e', 'below')} />
                    )}
                    <Line
                      type="linear"
                      dataKey="newUsers"
                      stroke="#22c55e"
                      strokeWidth={2}
                      dot={{ fill: '#22c55e', strokeWidth: 0, r: 3 }}
                      activeDot={{ r: 5, fill: '#22c55e' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Scroll hint for mobile */}
            {data.length > 30 && (
              <p className="text-xs text-muted-foreground text-center mt-2">
                ← Scroll horizontally to see more →
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
